import React, { useMemo, useState, useRef, useEffect } from 'react';
import { SortOption, Filter, GanttData, Loom } from '../types';

interface HeaderProps {
    sort: SortOption;
    onSortChange: (sort: SortOption) => void;
    filter: Filter;
    onFilterChange: (filter: Partial<Filter>) => void;
    onSetToday: () => void;
    isTodayCentered: boolean;
    data: GanttData;
    filteredLooms: Loom[];
}

const filterOptions = [
    { value: 'art', label: 'Articolo', adds: [{ label: 'Lavoro', value: '.StL' }, { label: 'Sched', value: '.StD' }] },
    { value: 'ord', label: 'Ordito' },
    { value: 'ann', label: 'Annodatura', adds: [{ label: 'Lavoro', value: '.StL' }] },
    { value: 'part', label: 'Part Tint' },
];

export const Header: React.FC<HeaderProps> = ({ sort, onSortChange, filter, onFilterChange, onSetToday, isTodayCentered, data, filteredLooms }) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleFilterTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const [type, add] = e.target.value.split('|');
        onFilterChange({ type: type as Filter['type'], add: add || undefined });
    };

    const suggestions = useMemo(() => {
        if (!data) return [];
        const query = filter.value.toUpperCase();
        const items = new Set<string>();

        if (filter.type === 'art') {
            Object.values(data.clothPiecesByWarpBeam).flat()
                .filter(p => !filter.add || p.displayStatus.statusClass === filter.add)
                .forEach(p => items.add(p.article));
        } else if (filter.type === 'ord') {
            Object.values(data.warpBeamsByLoom).flat().forEach(wb => items.add(wb.ordito));
        } else if (filter.type === 'ann') {
            Object.values(data.clothPiecesByWarpBeam).flat()
                .filter(p => !filter.add || p.displayStatus.statusClass === filter.add)
                .forEach(p => items.add(p.annodatura));
        } else if (filter.type === 'part') {
            Object.values(data.warpBeamsByLoom).flat().forEach(wb => items.add(wb.sb));
        }

        return Array.from(items)
            .filter(item => !query || item.toUpperCase().includes(query))
            .sort()
            .slice(0, 12);
    }, [data, filter.type, filter.value, filter.add]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
                inputRef.current && !inputRef.current.contains(e.target as Node)
            ) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const flangeSummary = useMemo(() => {
        if (!data || (filter.value.trim() === '' && !filter.add)) return { inLavoro: new Map(), schedulati: new Map() };
        const inLavoro = new Map<string, number>();
        const schedulati = new Map<string, number>();
        const filterValueUpper = filter.value.trim().toUpperCase();

        filteredLooms.forEach(loom => {
            const loomWarpBeams = data.warpBeamsByLoom[loom.id] || [];
            loomWarpBeams.forEach(wb => {
                const pieces = data.clothPiecesByWarpBeam[wb.id] || [];
                pieces.forEach(p => {
                    let match = false;
                    if (filter.type === 'art') {
                        if (filterValueUpper === '' || p.article.toUpperCase().includes(filterValueUpper)) {
                            if (!filter.add || filter.add === p.displayStatus.statusClass) match = true;
                        }
                    } else if (filter.type === 'ann') {
                        if (filterValueUpper === '' || p.annodatura.toUpperCase().includes(filterValueUpper)) {
                            if (!filter.add || filter.add === p.displayStatus.statusClass) match = true;
                        }
                    } else if (filter.type === 'ord' && filterValueUpper !== '' && wb.ordito.toUpperCase().includes(filterValueUpper)) {
                        match = true;
                    } else if (filter.type === 'part' && filterValueUpper !== '' && wb.sb.toUpperCase().includes(filterValueUpper)) {
                        match = true;
                    }
                    if (match) {
                        if (p.status === 'L' || wb.status === 'V') inLavoro.set(p.flange, (inLavoro.get(p.flange) || 0) + 1);
                        if (p.status === 'D') schedulati.set(p.flange, (schedulati.get(p.flange) || 0) + 1);
                    }
                });
            });
        });
        return { inLavoro, schedulati };
    }, [data, filter, filteredLooms]);

    const renderFlangeSummary = (summary: Map<string, number>) => {
        if (summary.size === 0) return <em>Nessun risultato</em>;
        return Array.from(summary.entries()).map(([flange, count]) => (
            <span key={flange} className="mr-2 font-bold text-indigo-600">{flange}:{count}</span>
        ));
    };

    return (
        <header className="relative flex-shrink-0 bg-gray-50 p-2 border-b border-gray-200 z-30 min-h-[60px]">
            <h1 className="sr-only">Programmazione telai tessili (Gantt)</h1>
            <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-4 w-full">
                
                {/* Sezione Sinistra: Controlli */}
                <div className="flex flex-wrap items-center gap-y-2 divide-x divide-gray-300">

                    <a href="/" className="pr-4 text-sm font-medium text-gray-600 hover:text-indigo-700 whitespace-nowrap">← Portfolio</a>

                    {/* Gruppo Ordinamento */}
                    <div className="flex items-center gap-3 pr-4">
                        <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Ordina per</label>
                        <div className="flex items-center p-0.5 rounded-lg bg-gray-200 gantt-sort-group-styled">
                            {(['Tel', 'Start', 'Stop'] as SortOption[]).map(option => (
                                <label key={option}>
                                    <input type="radio" name="sort" value={option} checked={sort === option} onChange={() => onSortChange(option)} className="sr-only" />
                                    <span className="block text-gray-800 rounded-md">
                                        {option === 'Tel' ? 'Telaio' : option === 'Start' ? 'Inizio' : 'Fine'}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                    
                    {/* Gruppo Filtro */}
                    <div className="flex items-center gap-4 pl-4">
                        <div className="flex items-center gap-2">
                            <label htmlFor="filter-type-select" className="text-sm font-medium text-gray-700">Filtra per</label>
                            <div className="flex items-center gantt-shadow-sm">
                                <select
                                    id="filter-type-select"
                                    onChange={handleFilterTypeChange}
                                    className="h-9 border border-gray-300 rounded-l-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                    value={`${filter.type}|${filter.add || ''}`}
                                >
                                    {filterOptions.map(opt => (
                                        <optgroup label={opt.label} key={opt.value}>
                                            <option value={`${opt.value}|`}>{opt.label}</option>
                                            {opt.adds?.map(addOpt => (
                                                <option key={addOpt.value} value={`${opt.value}|${addOpt.value}`}>
                                                    {opt.label} {addOpt.label}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                                <div className="relative">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        placeholder="Cerca..."
                                        value={filter.value}
                                        onChange={e => onFilterChange({ value: e.target.value })}
                                        onFocus={() => setShowSuggestions(true)}
                                        onKeyDown={e => { if (e.key === 'Escape') setShowSuggestions(false); }}
                                        className="h-9 px-3 border border-gray-300 rounded-r-md -ml-px focus:ring-indigo-500 focus:border-indigo-500 text-sm w-48"
                                        aria-label="Valore del filtro"
                                    />
                                    {showSuggestions && suggestions.length > 0 && (
                                        <div
                                            ref={dropdownRef}
                                            className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-300 rounded-md gantt-shadow-lg z-50 max-h-52 overflow-y-auto"
                                        >
                                            {suggestions.map(s => (
                                                <div
                                                    key={s}
                                                    className="px-3 py-1.5 text-sm cursor-pointer hover:bg-indigo-50 hover:text-indigo-700 font-mono"
                                                    onMouseDown={() => {
                                                        onFilterChange({ value: s });
                                                        setShowSuggestions(false);
                                                    }}
                                                >
                                                    {s}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {(filter.value.trim() !== '' || filter.add) && (
                            <div className="text-xs text-gray-600 space-y-0.5">
                                <div><span className="font-medium">In Lavoro:</span> {renderFlangeSummary(flangeSummary.inLavoro)}</div>
                                <div><span className="font-medium">Schedulati:</span> {renderFlangeSummary(flangeSummary.schedulati)}</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sezione Destra: Info e Azioni */}
                <div className="flex items-center gap-4">
                    <button
                        onClick={onSetToday}
                        disabled={isTodayCentered}
                        className={`flex items-center gap-2 h-9 px-4 font-semibold rounded-md text-sm gantt-shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${isTodayCentered ? 'bg-gray-300 text-gray-500 cursor-default' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span>Adesso</span>
                    </button>
                </div>
            </div>
        </header>
    );
};
